"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const TIME_SLOTS = [
  "09:00 AM", "10:30 AM", "01:00 PM", "02:30 PM", "04:00 PM", "05:30 PM"
];

const steps = ["Timing", "Details", "Summary", "Success"];

export function BookingDrawer({ open, onOpenChange }: { open: boolean, onOpenChange: (open: boolean) => void }) {
  const [step, setStep] = React.useState(0);
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [time, setTime] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: ""
  });
  const [isProcessing, setIsProcessing] = React.useState(false);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const handlePayment = async () => {
    setIsProcessing(true);
    // Simulation of checkout
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsProcessing(false);
    nextStep();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[500px] bg-background border-l border-white/10 p-0 overflow-hidden flex flex-col">
        <SheetHeader className="p-6 border-b border-white/5">
          <SheetTitle className="font-display text-3xl uppercase tracking-tight text-foreground flex items-center gap-3">
            <span className="text-[oklch(0.94_0.27_128)]">[001]</span>
            {steps[step] === "Success" ? "Confirmed" : `Secure Session / ${steps[step]}`}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto custom-scrollbar relative">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 space-y-8"
              >
                <div className="space-y-4">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Select Date</Label>
                  <div className="flex justify-center bg-white/5 p-4 rounded-md border border-white/10">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      className="rounded-md"
                      classNames={{
                        day_selected: "bg-[oklch(0.94_0.27_128)]! text-black!",
                        day_today: "bg-white/10 text-white",
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Select Time</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {TIME_SLOTS.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTime(t)}
                        className={cn(
                          "py-4 px-6 border font-mono text-sm transition-all duration-300",
                          time === t 
                            ? "bg-[oklch(0.94_0.27_128)] border-[oklch(0.94_0.27_128)] text-black" 
                            : "border-white/10 hover:border-[oklch(0.94_0.27_128)]/50 text-white"
                        )}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <Button 
                  disabled={!date || !time} 
                  onClick={nextStep}
                  className="w-full btn-acid py-8 text-xl mt-8"
                >
                  <span>→ Continue to Details</span>
                </Button>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 space-y-8"
              >
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Full Name</Label>
                    <Input 
                      placeholder="Enter your name" 
                      className="bg-white/5 border-white/10 h-14 font-serif text-lg focus-visible:ring-[oklch(0.94_0.27_128)]"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">E-mail Address</Label>
                    <Input 
                      type="email" 
                      placeholder="you@studio.com" 
                      className="bg-white/5 border-white/10 h-14 font-serif text-lg focus-visible:ring-[oklch(0.94_0.27_128)]"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Phone Number</Label>
                    <Input 
                      placeholder="+1 (555) 000-0000" 
                      className="bg-white/5 border-white/10 h-14 font-serif text-lg focus-visible:ring-[oklch(0.94_0.27_128)]"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <Button variant="outline" onClick={prevStep} className="flex-1 border-white/10 h-14 uppercase font-mono text-xs tracking-widest">Back</Button>
                  <Button 
                    disabled={!formData.name || !formData.email || !formData.phone} 
                    onClick={nextStep}
                    className="flex-[2] btn-acid h-14"
                  >
                    <span>→ Review Summary</span>
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 space-y-8"
              >
                <div className="glass p-8 space-y-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Offer</Label>
                      <h4 className="font-display text-2xl uppercase mt-1">THE SINGLE BUNDLE</h4>
                    </div>
                    <div className="text-right">
                      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Investment</Label>
                      <div className="font-display text-2xl text-[oklch(0.94_0.27_128)] mt-1">$ 1,350.00</div>
                    </div>
                  </div>

                  <div className="h-px bg-white/10" />

                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Date</span>
                      <span className="text-white font-mono text-xs uppercase tracking-widest">{date?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Time</span>
                      <span className="text-white font-mono text-xs uppercase tracking-widest">{time}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground font-mono text-xs uppercase tracking-widest">Client</span>
                      <span className="text-white font-mono text-xs uppercase tracking-widest">{formData.name}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                   <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Secure Checkout</Label>
                   <div className="bg-white/5 border border-white/10 p-4 rounded-md flex items-center justify-between opacity-50">
                     <span className="text-sm font-mono tracking-tighter">**** **** **** 4242</span>
                     <span className="text-xs font-mono uppercase">Stripe Secure</span>
                   </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <Button variant="outline" onClick={prevStep} className="flex-1 border-white/10 h-14 uppercase font-mono text-xs tracking-widest">Edit</Button>
                  <Button 
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="flex-[2] btn-acid h-14 acid-glow"
                  >
                    <span>{isProcessing ? "Processing..." : "→ Confirm & Pay"}</span>
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-6 h-full flex flex-col items-center justify-center text-center space-y-12 py-20"
              >
                <div className="relative w-full aspect-square max-w-[300px] flex items-center justify-center">
                  {/* Guitar Outline Animation */}
                  <svg
                    viewBox="0 0 100 100"
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <motion.path
                      d="M50 5 C55 5, 58 10, 58 15 L58 35 C65 35, 75 45, 75 65 C75 85, 65 95, 50 95 C35 95, 25 85, 25 65 C25 45, 35 35, 42 35 L42 15 C42 10, 45 5, 50 5 Z"
                      stroke="var(--acid)"
                      strokeWidth="0.5"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 2, ease: "easeInOut" }}
                    />
                    <motion.path
                      d="M42 35 L58 35 M45 15 L55 15 M45 20 L55 20 M45 25 L55 25"
                      stroke="var(--acid)"
                      strokeWidth="0.5"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 0.5 }}
                      transition={{ duration: 1, delay: 1 }}
                    />
                  </svg>

                  {/* Vinyl Record 3D Animation */}
                  <motion.div
                    className="w-48 h-48 bg-[#111] rounded-full border-[10px] border-[#222] relative flex items-center justify-center shadow-2xl"
                    initial={{ rotateY: 90, scale: 0 }}
                    animate={{ rotateY: 0, scale: 1, rotate: 360 }}
                    transition={{ 
                      rotateY: { duration: 1, ease: "easeOut" },
                      scale: { duration: 1, ease: "easeOut" },
                      rotate: { duration: 5, repeat: Infinity, ease: "linear" }
                    }}
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <div className="w-40 h-40 rounded-full border border-white/5" />
                    <div className="w-32 h-32 rounded-full border border-white/5" />
                    <div className="absolute w-16 h-16 bg-[oklch(0.94_0.27_128)] rounded-full flex items-center justify-center overflow-hidden">
                       <span className="font-display text-[8px] text-black uppercase tracking-tighter">VOID STUDIO</span>
                    </div>
                    <div className="absolute w-2 h-2 bg-background rounded-full" />
                  </motion.div>
                </div>

                <div className="space-y-4">
                  <h2 className="font-display text-5xl md:text-6xl uppercase leading-none tracking-tighter">CONGRATULATIONS!</h2>
                  <p className="font-serif text-2xl text-muted-foreground leading-tight max-w-xs mx-auto">
                    Your session is locked for <span className="text-white">{date?.toLocaleDateString()}</span> at <span className="text-white">{time}</span>. 
                    <br />
                    See you at the Studio.
                  </p>
                </div>

                <Button 
                  onClick={() => onOpenChange(false)}
                  className="btn-acid px-12 py-6 text-lg"
                >
                  <span>Close & Exit</span>
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SheetContent>
    </Sheet>
  );
}
